import { useEffect } from 'react';

import { Modal, Pressable, useWindowDimensions } from 'react-native';

import { XStack, YStack } from 'tamagui';

import { HeadingS, Icon } from '@south-street-app/atoms';
import { useChatStore } from '@south-street-app/stores';
import { isWeb, refreshUnreadCounts } from '@south-street-app/utils';

import { ArchivedChatsList } from '../ArchivedChatsScreen/ArchivedChatsList';

type ArchivedChatsModalProps = {
  visible: boolean;
  onClose: () => void;
  onPressConversation: (uniqueName: string) => void;
};

const ArchivedChatsModal = ({
  visible,
  onClose,
  onPressConversation,
}: ArchivedChatsModalProps) => {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const modalWidth = Math.min(700, screenWidth * 0.9);
  const modalHeight = Math.min(780, screenHeight * 0.9);

  const archivedConversations = useChatStore(
    (state) => state.archivedConversations,
  );
  const inboxRefreshKey = useChatStore((state) => state.inboxRefreshKey);

  useEffect(() => {
    if (visible) {
      useChatStore.getState().bumpInboxRefreshKey();

      refreshUnreadCounts().catch(() => undefined);
    }
  }, [visible]);

  useEffect(() => {
    if (!isWeb || !visible) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, onClose]);

  return (
    <Modal
      animationType={"fade"}
      onRequestClose={onClose}
      transparent
      visible={visible}
    >
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{ width: modalWidth, height: modalHeight, display: 'flex', flexDirection: 'column' }}
        >
          <YStack
            backgroundColor={'$mono100'}
            borderRadius={'$true'}
            elevation={8}
            flex={1}
            overflow={'hidden'}
            shadowColor={'$mono900'}
            shadowOffset={{ width: 0, height: 4 }}
            shadowOpacity={0.15}
            shadowRadius={16}
          >
            <XStack
              alignItems={'center'}
              borderBottomColor={'$mono300'}
              borderBottomWidth={1}
              justifyContent={'space-between'}
              paddingHorizontal={'$4'}
              paddingVertical={'$4'}
            >
              <HeadingS color={'$mono900'}>{"Archived Chats"}</HeadingS>
              <YStack
                borderRadius={'$full'}
                cursor={'pointer' as const}
                onPress={onClose}
                padding={'$1'}
                pressStyle={{ opacity: 0.7 }}
              >
                <Icon color={'$mono500'} iconName={'closeIcon'} size={'$5'} />
              </YStack>
            </XStack>

            <YStack flex={1}>
              <ArchivedChatsList
                conversations={archivedConversations}
                onPressConversation={onPressConversation}
                refreshKey={inboxRefreshKey}
              />
            </YStack>
          </YStack>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export { ArchivedChatsModal };
